export const chatbotKnowledge = {
  "walkthrough": [
    {
      "step": 1,
      "title": "Obtaining the Fleet List",
      "content": "The foundation of a successful analysis is the fleet list. Offer it as a 'complimentary fleet analysis' to both existing customers for retention and prospects to break the ice. All you need is the VIN and Mileage (or Year/Make/Model/Mileage).",
      "talkingPoints": [
        "A fleet list reveals buying habits, brand preferences, and cycle strategies.",
        "It helps you identify 'hidden' opportunities like executive or personal vehicles often tucked into business lists."
      ]
    },
    {
      "step": 2,
      "title": "Uploading & Baseline",
      "content": "Go to www.fleet-analysis.com, use the CSV template, and upload. This creates your 'Current Situation' baseline. Be prepared for the 'Shock Effect'—most customers have no idea what their fleet actually costs them until they see the data.",
      "talkingPoints": [
        "Unmanaged fleets typically keep vehicles for 11 to 11.5 years.",
        "Data is a cold, hard fact. As they say, 'Data doesn't lie, but opinions might.'",
        "Average model age and current cycle years (e.g., 10+ years) are your primary 'pain points' to highlight."
      ]
    },
    {
      "step": 3,
      "title": "Maintenance Analysis",
      "content": "Explain our conservative pre-COVID estimates to maintain credibility. The goal is to keep vehicles under warranty to avoid 'spikes' in out-of-pocket repair costs and reduce downtime.",
      "talkingPoints": [
        "Maintenance Tiers: 0-50k miles = 4¢/mile, 50k-100k = 5¢/mile, 100k-150k = 9¢/mile, 150k+ = 12-14¢/mile.",
        "Remind customers: 'You pay twice for repairs—once for the shop bill, and once for the downtime of that vehicle being out of service.'"
      ]
    },
    {
      "step": 4,
      "title": "Fuel Analysis & Degradation",
      "content": "Fuel is often the largest variable expense. Introduce 'Fuel Economy Degradation': engines lose up to 0.5 MPG per year as they age due to mechanical wear, even with perfect maintenance.",
      "talkingPoints": [
        "Replacing an 8-10 year old vehicle can immediately boost efficiency by 4-5 MPG.",
        "This isn't just a cost; it's a 'rolling billboard' for their business—newer vehicles look better and reflect a professional image."
      ]
    },
    {
      "step": 5,
      "title": "The Sweet Spot",
      "content": "Identify the 'Sweet Spot' (93,000 miles for light-duty trucks). This is the optimal window where the vehicle has maximum utilization but retains high residual value and transferable warranty.",
      "talkingPoints": [
        "At 93k miles, there's still warranty left, which increases the buyer pool and makes banks more willing to lend to the next owner.",
        "Exiting at the sweet spot ensures the customer 'cycles out' before the value bottoms out to scrap metal."
      ]
    },
    {
      "step": 6,
      "title": "Funding with Track Lease",
      "content": "The 'Track Lease' (or Equity Lease) is a game-changer. It allows customers to pay for only the portion of the vehicle they use, preserving cash flow for their core business operations.",
      "talkingPoints": [
        "Show them the math: For the same budget as buying 4 trucks with cash, they might be able to lease 12 on a track lease.",
        "Emphasize the 'Consultative Partner' role: You aren't just selling a truck; you're helping them find $100k+ in bottom-line savings."
      ]
    }
  ],
  "faqs": [
    {
      "question": "What is the success rate of using this tool?",
      "answer": "Presentations that include the fleet analysis close at an impressive 72% success rate. It shifts the conversation from 'price/discount' to 'consultative savings'.",
      "actions": ["Start Walkthrough", "Explain Metrics"]
    },
    {
      "question": "What is the Sweet Spot for light duty trucks?",
      "answer": "The sweet spot is 93,000 miles. At this point, the vehicle still has transferable warranty life, which maximizes resale value and ensures more lenders are willing to finance the next buyer.",
      "actions": ["Explain Metrics", "How Maintenance Spikes?"]
    },
    {
      "question": "How do maintenance costs increase over time?",
      "answer": "We use conservative tiers: 4¢/mile (0-50k), 5¢/mile (50k-100k), and 9¢/mile (100k-150k). Beyond 150k miles, costs spike to 12-14¢/mile, not including the 'double cost' of vehicle downtime.",
      "actions": ["The Sweet Spot", "Fuel Degradation?"]
    },
    {
      "question": "Explain Fuel Economy Degradation.",
      "answer": "Engines are machines that lose efficiency with age. We estimate a loss of up to 0.5 MPG per year. An older fleet is often 'pouring money' into fuel that a newer fleet would save as pure profit.",
      "actions": ["Maintenance Costs", "Start Walkthrough"]
    },
    {
      "question": "Why use a Track Lease instead of Cash?",
      "answer": "A Track Lease (Equity Lease) preserves capital. Customers can often acquire 2-3 times more vehicles for the same annual budget as a cash purchase, while building equity at the end of the term.",
      "actions": ["The Sweet Spot", "Success Rate"]
    },
    {
      "question": "Who should I present this analysis to?",
      "answer": "Target decision-makers: CEOs and CFOs. They care about bottom-line profitability and cash flow. When the CFO sees you know more about their fleet costs than they do, you become a valued partner."
    },
    {
      "question": "How do I handle the 'I buy used' objection?",
      "answer": "Explain that the 'Sweet Spot' doesn't change. Buying used often means starting closer to the high-maintenance/low-efficiency tiers, which creates a vulnerability in their business plan."
    },
    {
      "question": "What is the One-Stop Shop concept?",
      "answer": "Your dealership provides more than metal. You offer a holistic solution: Vehicles, Maintenance Management, Fuel Programs (cards/tracking), and Telematics for driver behavior."
    },
    {
      "question": "What are the Four Pillars of Fleet Management?",
      "answer": "The Four Pillars are: 1. Vehicles (Acquisition/Funding), 2. Maintenance, 3. Fuel, and 4. Driver Behavior (Telematics). Focusing on all four allows for holistic TCO reduction.",
      "actions": ["Track Lease vs Cash", "Explain Metrics"]
    },
    {
      "question": "Where can I find training and certification?",
      "answer": "Go to the 'Dealer Resources' section on the website. There you'll find playback links, tutorials, a presentation outline, and the E-learning series for certification."
    },
    {
      "question": "What are the Top 5 Insights of the Cash Flow Analysis?",
      "answer": "1. Lower Total Cost of Ownership (TCO), 2. Improved cash flow predictability, 3. Reduced maintenance and repair costs, 4. Increased fuel efficiency, and 5. Enhanced asset utilization.",
      "actions": ["Explain Metrics", "Start Walkthrough"]
    },
    {
      "question": "Why do we use pre-COVID maintenance numbers?",
      "answer": "We use conservative pre-COVID numbers to maintain credibility and avoid overestimating costs. Customers are often shocked to find their actual spending is much higher than our conservative estimates.",
      "actions": ["Maintenance Costs", "Explain Metrics"]
    }
  ],
  "metrics": {
    "unmanaged_cycle": "11-11.5 years",
    "managed_cycle": "3-4 years (48 months)",
    "maintenance_tier_1": "4 cents (0-50k)",
    "maintenance_tier_2": "5 cents (50k-100k)",
    "maintenance_tier_3": "9 cents (100k-150k)",
    "maintenance_tier_4": "12-14 cents (150k+)",
    "fuel_degradation": "0.5 MPG per year",
    "sweet_spot_miles": 93000,
    "success_rate": "72%"
  }
};

